// react
import { useCallback, useMemo, useState } from 'react';

// components
import { Dialog } from '@karehero/llama/components/atoms/Dialog/Dialog';
import { createFieldHelper } from '@karehero/llama/components/organisms/EditorFields/fieldHelper';
import { Editor } from '@karehero/llama/components/organisms/Editor/Editor';
import AdminTablePage from '@karehero/llama/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';

// store
import {
  useGetAllActionPlanQuery,
  useUpdateActionPlanMutation,
} from 'store/api/actionPlan';
import { ActionPlanItem, FieldType } from '@karehero/models';

// columns
const columns: GridColDef[] = [
  { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 80 },
  { field: 'title', headerName: 'Title', flex: 1, minWidth: 150 },
  { field: 'sanitySlug', headerName: 'Sanity Slug', flex: 1, minWidth: 150 },
  { field: 'route', headerName: 'Route', flex: 1, minWidth: 150 },
];

const fields = createFieldHelper<ActionPlanItem>({
  sanitySlug: {
    label: 'Sanity Slug',
    field: {
      type: FieldType.Text,
    },
  },
  route: {
    label: 'Route',
    field: {
      type: FieldType.Text,
    },
  },
});

export const ActionPlanTable = ({
  isEditable = true,
}: {
  isEditable?: boolean;
}) => {
  // hooks
  const { data: actionPlanItems } = useGetAllActionPlanQuery();
  const [updateActionPlan] = useUpdateActionPlanMutation();

  // state
  const [selectedActionPlanItem, setSelectedActionPlanItem] = useState<
    Partial<ActionPlanItem>
  >({});
  const [isActionPlanItemEditorOpen, setIsActionPlanItemEditorOpen] =
    useState(false);

  // methods
  const handleSave = useCallback(() => {
    updateActionPlan?.({
      ...selectedActionPlanItem,
    } as ActionPlanItem);
    setIsActionPlanItemEditorOpen(false);
  }, [selectedActionPlanItem, updateActionPlan]);

  // memo
  const rows = useMemo(() => {
    return actionPlanItems || [];
  }, [actionPlanItems]);

  return (
    <AdminTablePage title='Action Plan Items'>
      <DataTable
        columns={columns}
        rows={rows}
        actions={
          isEditable
            ? [
                {
                  label: 'Edit',
                  icon: 'edit',
                  onPress: (actionPlanItem: ActionPlanItem) => {
                    setSelectedActionPlanItem({ ...actionPlanItem });
                    setIsActionPlanItemEditorOpen(true);
                  },
                },
              ]
            : []
        }
      />
      <Dialog
        title='Edit Action Plan Item'
        isCloseButton
        isOpen={isActionPlanItemEditorOpen}
        setIsOpen={setIsActionPlanItemEditorOpen}
      >
        <Editor
          onSave={handleSave}
          onCancel={() => setIsActionPlanItemEditorOpen(false)}
          saveLabel='Save'
          cancelLabel='Cancel'
          isWithArrows={false}
          isActionsDisabled={false}
          value={selectedActionPlanItem}
          onChange={(res) => {
            setSelectedActionPlanItem((prev) => ({
              ...prev,
              ...res,
            }));
          }}
          fields={fields}
          isFloatingActions={true}
          isForm={true}
        />
      </Dialog>
    </AdminTablePage>
  );
};

export default ActionPlanTable;
